'use client';

import React, { useContext, useEffect, useState } from 'react';
import AttentiveScript from '~/components/ThirdPartyScripts/AttentiveScript';
import BingScript from '~/components/ThirdPartyScripts/BingScript';
import FacebookPixel from '~/components/ThirdPartyScripts/FacebookPixel';
import GoogleAnalyticsScripts from '~/components/ThirdPartyScripts/GoogleAnalyticsScripts';
import ImpactTracker from '~/components/ThirdPartyScripts/ImpactTracker';
import MntnTrackingPixel from '~/components/ThirdPartyScripts/MntnTrackingPixel';
import RedditScript from '~/components/ThirdPartyScripts/RedditScript';
import TikTokScript from '~/components/ThirdPartyScripts/TikTokScript';
import { setKnownCookie } from '~/hooks/cookies';
import {
  CCPA_OPT_IN_VALUE,
  CCPA_OPT_OUT_VALUE,
  createPrivacyConsentModel,
  PrivacyConsentModel,
  shouldLoadThirdPartyScripts,
} from '~/lib/privacy/model';
import { initializeAmplitudeSdk } from '~/services/analytics/providers/amplitude';

type IPrivacyConsentContext = {
  state: PrivacyConsentModel;
  setState: React.Dispatch<React.SetStateAction<PrivacyConsentModel>>;
};

const PrivacyConsentContext = React.createContext<IPrivacyConsentContext>({
  state: {
    state: 'unknown',
  },
  setState: () => {},
});

export function PrivacyConsentProvider({
  children,
  initialModel,
}: {
  children: React.ReactNode;
  initialModel: PrivacyConsentModel;
}) {
  const [model, setModel] = useState(() => {
    if (
      initialModel.state === 'unknown' &&
      typeof navigator !== 'undefined' &&
      'globalPrivacyControl' in navigator
    ) {
      const optOut = !!navigator.globalPrivacyControl;

      const cookieValue = optOut ? CCPA_OPT_OUT_VALUE : CCPA_OPT_IN_VALUE;
      setKnownCookie(undefined, 'ccpa', cookieValue);
      return createPrivacyConsentModel(cookieValue);
    }
    return initialModel;
  });

  useEffect(() => {
    if (shouldLoadThirdPartyScripts(model)) {
      initializeAmplitudeSdk();
    }
  }, [model.state]);

  return (
    <PrivacyConsentContext
      value={{
        state: model,
        setState: setModel,
      }}
    >
      {children}

      {shouldLoadThirdPartyScripts(model) && (
        <>
          <ImpactTracker />
          <GoogleAnalyticsScripts />
          <RedditScript />
          <TikTokScript />
          <AttentiveScript />
          <MntnTrackingPixel />
          <BingScript />
          <FacebookPixel />
        </>
      )}
    </PrivacyConsentContext>
  );
}

export function usePrivacyConsent() {
  const context = useContext(PrivacyConsentContext);
  if (!context) {
    throw new Error(
      'usePrivacyConsent must be used within a PrivacyConsentProvider',
    );
  }
  return context;
}
